import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2, MessageSquare, Send, X } from 'lucide-react'
import ErrorBanner from '@/components/ErrorBanner'
import { useLanguage } from '@/contexts/LanguageContext'
import { logError, responseErrorMessage, toUserMessage } from '@/utils/errorMessage'
import { turbineService, type Comment } from '@/services/turbineService'
import { authService } from '@/services/authService'

// Comment Sidebar Component (same as in Turbine360ViewScreen)
const CommentSidebar: React.FC<{
  turbineId: number
  isOpen: boolean
  onClose: () => void
}> = ({ turbineId, isOpen, onClose }) => {
  const { t } = useLanguage()
  const [comments, setComments] = useState<Comment[]>([])
  const [newComment, setNewComment] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [user, setUser] = useState<any>(null)
  const [editingComment, setEditingComment] = useState<number | null>(null)
  const [editText, setEditText] = useState('')
  const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set())
  // Liste yukleme hatasi ile islem (ekle/guncelle/sil) hatasi ayri tutulur:
  // biri listenin ustunde "tekrar dene" ile, digeri formun yaninda gosterilir.
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  // Load comments and user data
  const loadData = useCallback(async (showLoader: boolean = true) => {
    if (showLoader) setLoading(true)
    setLoadError(null)
    try {
      const [commentsResponse, userData] = await Promise.all([
        turbineService.getComments(turbineId),
        authService.getCurrentUser()
      ])

      if (commentsResponse.success) {
        setComments(commentsResponse.data || [])
      } else {
        // success:false bir hatadir. Mevcut yorumlar KORUNUR; bos liste
        // gostermek "hic yorum yok" yalanini soylerdi.
        setLoadError(responseErrorMessage(commentsResponse, t, t.errors.loadFailed))
      }
      setUser(userData)
    } catch (error) {
      logError('CommentSidebar.loadData', error)
      setLoadError(toUserMessage(error, t, t.errors.loadFailed))
    } finally {
      if (showLoader) setLoading(false)
    }
  }, [turbineId, t])

  useEffect(() => {
    if (isOpen) {
      setActionError(null)
      loadData()
    }
    // Yalnizca panel acildiginda / turbin degistiginde yuklenir; loadData
    // kimligi dil secimi degisince de degisir, bu yeniden cekmeyi tetiklemesin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, turbineId])

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newComment.trim() || submitting) return

    setSubmitting(true)
    setActionError(null)
    try {
      const response = await turbineService.addComment(turbineId, newComment.trim())
      if (response.success) {
        // Servis eklenen yorumu `comment` veya `data` alaninda dondurebiliyor;
        // ikisi de bossa liste sunucudan tazelenir ki kullanici yorumunun
        // gercekten kaydedildigini gorsun.
        const created = response.comment ?? response.data
        setNewComment('')
        if (created) {
          setComments(prev => [created, ...prev])
        } else {
          await loadData(false)
        }
      } else {
        // Onceden bu dal tamamen sessizdi: yorum kaydedilmemisken kullanici
        // kaydedildigini saniyordu.
        setActionError(responseErrorMessage(response, t, 'Yorum eklenemedi.')) // TODO(i18n)
      }
    } catch (error) {
      logError('CommentSidebar.addComment', error)
      setActionError(toUserMessage(error, t, 'Yorum eklenemedi.')) // TODO(i18n)
    } finally {
      setSubmitting(false)
    }
  }

  const handleEditComment = async (commentId: number) => {
    if (!editText.trim() || submitting) return

    setSubmitting(true)
    setActionError(null)
    try {
      const response = await turbineService.updateComment(turbineId, commentId, editText.trim())
      if (response.success) {
        setComments(prev => prev.map(comment =>
          comment.id === commentId
            ? { ...comment, comment: editText.trim(), updated_at: new Date().toISOString() }
            : comment
        ))
        setEditingComment(null)
        setEditText('')
      } else {
        // Hata durumunda duzenleme alani KAPANMAZ: kullanicinin metni kaybolmasin.
        setActionError(responseErrorMessage(response, t, 'Yorum güncellenemedi.')) // TODO(i18n)
      }
    } catch (error) {
      logError('CommentSidebar.updateComment', error)
      setActionError(toUserMessage(error, t, 'Yorum güncellenemedi.')) // TODO(i18n)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteComment = async (commentId: number) => {
    if (submitting) return
    if (!confirm(t.confirmDeleteComment)) return

    setSubmitting(true)
    setActionError(null)
    try {
      const response = await turbineService.deleteComment(turbineId, commentId)
      if (response.success) {
        setComments(prev => prev.filter(comment => comment.id !== commentId))
      } else {
        // Yikici islem: hata varsa yorum listede KALIR ve neden gosterilir.
        setActionError(responseErrorMessage(response, t, 'Yorum silinemedi.')) // TODO(i18n)
      }
    } catch (error) {
      logError('CommentSidebar.deleteComment', error)
      setActionError(toUserMessage(error, t, 'Yorum silinemedi.')) // TODO(i18n)
    } finally {
      setSubmitting(false)
    }
  }

  const toggleExpanded = (commentId: number) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev)
      if (newSet.has(commentId)) {
        newSet.delete(commentId)
      } else {
        newSet.add(commentId)
      }
      return newSet
    })
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[9998]"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-[9999] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-blue-600" />
                {t.turbineDetail.photos.comments}
              </h3>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Hata panelin ICINDE kalir: sayfa govdesindeki bir kutu bu
                  panelin backdrop'unun altinda gorunmez olurdu. */}
              <ErrorBanner
                message={loadError}
                onRetry={() => loadData()}
                onDismiss={() => setLoadError(null)}
              />

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : comments.length === 0 ? (
                // Yukleme hata verdiyse "henuz yorum yok" GOSTERILMEZ; ustteki
                // hata kutusu zaten gercek nedeni soyluyor.
                loadError ? null : (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>{t.noCommentsYet}</p>
                  </div>
                )
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{comment.user_name}</p>
                        <p className="text-xs text-gray-500">
                          {(() => {
                            const createdDate = new Date(comment.created_at)
                            if (!isNaN(createdDate.getTime())) {
                              // Format: "21 Aralık 2025 - 14:50"
                              const formattedDate = createdDate.toLocaleDateString('tr-TR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })
                              const timeString = createdDate.toLocaleTimeString('tr-TR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                              return `${formattedDate} - ${timeString}`
                            }
                            return ''
                          })()}
                        </p>
                      </div>
                      {user?.id === comment.user_id && (
                        <div className="flex space-x-1">
                          <button
                            onClick={() => {
                              setEditingComment(comment.id)
                              setEditText(comment.comment)
                            }}
                            className="text-gray-400 hover:text-blue-500 transition-colors p-1" title={t.editComment}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            disabled={submitting}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1 disabled:opacity-50 disabled:cursor-not-allowed" title={t.deleteComment}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      )}
                    </div>

                    {editingComment === comment.id ? (
                      <div className="space-y-2">
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm resize-none"
                          rows={3}
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditComment(comment.id)}
                            disabled={submitting}
                            className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50"
                          >
                            {submitting ? t.saving : t.save}
                          </button>
                          <button
                            onClick={() => {
                              setEditingComment(null)
                              setEditText('')
                            }}
                            className="px-3 py-1 bg-gray-300 text-gray-700 text-xs rounded-lg hover:bg-gray-400"
                          >
                            {t.cancel}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className={`text-sm text-gray-700 ${
                          comment.comment && comment.comment.length > 100 && !expandedComments.has(comment.id)
                            ? 'line-clamp-3'
                            : ''
                        }`}>
                          {expandedComments.has(comment.id)
                            ? (comment.comment || '')
                            : (comment.comment && comment.comment.length > 100)
                              ? comment.comment.substring(0, 100) + '...'
                              : (comment.comment || '')
                          }
                        </p>
                        {comment.comment && comment.comment.length > 100 && (
                          <button
                            onClick={() => toggleExpanded(comment.id)}
                            className="text-xs text-blue-600 hover:text-blue-800 mt-1"
                          >
                            {expandedComments.has(comment.id) ? t.showLess : t.readMore}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Add Comment Form */}
            <form onSubmit={handleSubmitComment} className="p-4 border-t bg-gray-50">
              {/* Ekleme / guncelleme / silme hatasi formun hemen ustunde:
                  kullanicinin baktigi yer burasi. */}
              <ErrorBanner
                message={actionError}
                onDismiss={() => setActionError(null)}
                className="mb-3"
              />

              <div className="flex space-x-2">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={t.writeComment}
                  className="flex-1 p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || submitting}
                  className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default CommentSidebar
